/**
 * Serve Details Widget - Embeddable Bundle Entry Point
 *
 * One page for woodsidebible.org/serve/<Opportunity_ID> (WordPress 301s the
 * old /serve/details/?id= there),
 * replacing the stock <mpp-opportunity-details>. Same shadow-DOM shell as
 * the finder (shell.tsx), same API, same components.
 *
 * Embed:
 *   <div id="serve-details-root"></div>
 *   <script src="https://widgets.woodsidebible.org/serve-finder/details.js"></script>
 *
 * The opportunity comes from `data-opportunity` or, by default, the page's
 * `?id=` query param (what the finder links to and what the stock widget
 * used). `data-return-url` (stock `returnurl`) is where "All opportunities"
 * goes; `data-show-full-address` (stock `showfulladdress`) adds the street
 * address.
 */

import React from 'react';
import { CarePage } from '../src/components/CarePage';
import { CollectionPage } from '../src/components/CollectionPage';
import { ServeDetails } from '../src/components/ServeDetails';
import { attr } from './params';
import { ShadowWidget } from './shell';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  opportunityId?: number;
  finderUrl?: string;
  showFullAddress?: boolean;
}

declare global {
  interface Window {
    SERVE_DETAILS_WIDGET_CONFIG?: WidgetConfig;
    ServeDetailsWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'serve-details-root';
const LEGACY_CONTAINER_ID = 'serveDetails';

const truthy = (v: string | undefined) => !!v && /^(1|true|yes|on)$/i.test(v.trim());

function opportunityIdFromPage(container: HTMLElement, config: WidgetConfig): number | null {
  const fromAttr = attr(container.dataset.opportunity);
  let raw: string | undefined = config.opportunityId ? String(config.opportunityId) : fromAttr;
  if (!raw) {
    try {
      raw = new URLSearchParams(window.location.search).get('id') ?? undefined;
    } catch {
      /* no URL API */
    }
  }
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * WHAT THIS PAGE IS FOR, decided before anything renders:
 *   /care, /care/{programme}[/{campus}] or data-care   → the care pages
 *   /serve/{slug} (not /serve/details/) or data-collection → a Collection
 *     (data-layout="section": as a section of the page, not the whole page;
 *     data-layout="sheet": only the sign-up sheet, opened by the page's own
 *     #signup-serve / #signup-receive links)
 *   anything else                                        → one opportunity by
 *     ?id=, exactly as before (an id that is a Collection shows its page)
 * WordPress sends /serve/{slug} and /care/... here through its request filter.
 * A real WordPress page at one of those paths still wins.
 */
type View =
  | { kind: 'care'; ref: string }
  | { kind: 'collection'; ref: string }
  | { kind: 'opportunity'; id?: number };

function viewFromPage(container: HTMLElement): View {
  const care = attr(container.dataset.care);
  if (care !== undefined) return { kind: 'care', ref: care };
  const collection = attr(container.dataset.collection);
  if (collection) return { kind: 'collection', ref: collection };
  let path = '';
  let query: URLSearchParams | null = null;
  try {
    path = window.location.pathname.replace(/\/+$/, '');
    query = new URLSearchParams(window.location.search);
  } catch {
    /* no URL API */
  }
  const qCollection = query?.get('collection');
  if (qCollection) return { kind: 'collection', ref: qCollection };
  const qCare = query?.get('care');
  if (qCare !== null && qCare !== undefined) return { kind: 'care', ref: qCare };
  const careMatch = path.match(/^\/care(?:\/(.+))?$/);
  if (careMatch) return { kind: 'care', ref: careMatch[1] ?? '' };
  // /serve/4031: an opportunity by its short address. An id that turns out to
  // be a Collection still shows the Collection (ServeDetails hands it over).
  const idMatch = path.match(/^\/serve\/(\d{1,9})$/);
  if (idMatch) return { kind: 'opportunity', id: Number(idMatch[1]) };
  const serveMatch = path.match(/^\/serve\/(.+)$/);
  if (serveMatch && !/^details(\/|$)/.test(serveMatch[1]))
    return { kind: 'collection', ref: serveMatch[1] };
  return { kind: 'opportunity' };
}

function createDetailsWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  return new ShadowWidget({
    containerIds: config.containerId
      ? [config.containerId]
      : [DEFAULT_CONTAINER_ID, LEGACY_CONTAINER_ID],
    overlayHostId: 'serve-details-overlay',
    fontId: 'serve-finder-widget-montserrat',
    scrollLockFlag: '__SERVE_DETAILS_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Serve Details Widget',
    render: (container) => {
      const view = viewFromPage(container);
      const finderUrl =
        config.finderUrl ??
        attr(container.dataset.finderUrl) ??
        attr(container.dataset.returnUrl) ??
        attr(container.dataset.returnurl) ??
        'https://woodsidebible.org/serve/';
      if (view.kind === 'care') return <CarePage apiBaseUrl={apiBaseUrl} reference={view.ref} />;
      if (view.kind === 'collection')
        return (
          <CollectionPage
            apiBaseUrl={apiBaseUrl}
            reference={view.ref}
            finderUrl={finderUrl}
            layout={
              attr(container.dataset.layout) === 'section'
                ? 'section'
                : attr(container.dataset.layout) === 'sheet'
                  ? 'sheet'
                  : 'page'
            }
          />
        );
      return (
        <ServeDetails
          apiBaseUrl={apiBaseUrl}
          opportunityId={view.id ?? opportunityIdFromPage(container, config)}
          finderUrl={
            config.finderUrl ??
            attr(container.dataset.finderUrl) ??
            attr(container.dataset.returnUrl) ??
            attr(container.dataset.returnurl)
          }
          showFullAddress={
            config.showFullAddress ??
            (truthy(container.dataset.showFullAddress) || truthy(container.dataset.showfulladdress))
          }
        />
      );
    },
  });
}

if (typeof window !== 'undefined') {
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.SERVE_DETAILS_WIDGET_CONFIG || {}),
  };
  window.SERVE_DETAILS_WIDGET_CONFIG = config;

  if (!window.ServeDetailsWidget) {
    const boot = () => {
      const widget = createDetailsWidget(config);
      widget.init();
      window.ServeDetailsWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
